import { useCreateUser } from '@/remote/user-management/create-user';
import { useDeleteUser } from '@/remote/user-management/delete-user';
import { useUsersQuery } from '@/remote/user-management/fetch-users';
import { useUpdateUser } from '@/remote/user-management/update-user';
import type { NewUser, User } from '@/types/site';

import { useMemo, useState } from 'react';

// Transform API data to User type

export type SortField = keyof User;
export type SortDirection = 'asc' | 'desc';

interface UseUserDataReturn {
  users: User[];
  filteredUsers: User[];
  sortedUsers: User[];
  currentUsers: User[];
  searchTerm: string;
  sortField: SortField;
  sortDirection: SortDirection;
  currentPage: number;
  itemsPerPage: number;
  totalPages: number;
  startIndex: number;
  endIndex: number;
  actions: {
    setSearchTerm: (term: string) => void;
    handleSort: (field: SortField) => void;
    setCurrentPage: (page: number) => void;
    setItemsPerPage: (items: number) => void;
    addUser: (user: NewUser) => Promise<void>;
    updateUser: (userId: string, userData: Partial<NewUser>) => Promise<void>;
    deleteUser: (userId: string) => Promise<void>;
  };
  mutations: {
    createUser: ReturnType<typeof useCreateUser>;
    updateUser: ReturnType<typeof useUpdateUser>;
    deleteUser: ReturnType<typeof useDeleteUser>;
  };
}

export const useUserData = (): UseUserDataReturn => {
  const { data: users = [] } = useUsersQuery();
  const createUserMutation = useCreateUser();
  const updateUserMutation = useUpdateUser();
  const deleteUserMutation = useDeleteUser();

  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<SortField>('id');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const filteredUsers = useMemo(() => {
    return users.filter((user) => {
      const searchLower = searchTerm.toLowerCase();
      return (
        user.name.toLowerCase().includes(searchLower) ||
        user.mobilePhone.toLowerCase().includes(searchLower) ||
        user.phone.toLowerCase().includes(searchLower) ||
        user.loginId.toLowerCase().includes(searchLower)
      );
    });
  }, [users, searchTerm]);

  const sortedUsers = useMemo(() => {
    return [...filteredUsers].sort((a, b) => {
      const aValue = a[sortField];
      const bValue = b[sortField];

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }

      if (typeof aValue === 'boolean' && typeof bValue === 'boolean') {
        return sortDirection === 'asc'
          ? aValue === bValue
            ? 0
            : aValue
              ? 1
              : -1
          : aValue === bValue
            ? 0
            : aValue
              ? -1
              : 1;
      }

      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();

      if (sortDirection === 'asc') {
        return aStr < bStr ? -1 : aStr > bStr ? 1 : 0;
      } else {
        return aStr > bStr ? -1 : aStr < bStr ? 1 : 0;
      }
    });
  }, [filteredUsers, sortField, sortDirection]);

  const totalPages = Math.ceil(sortedUsers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentUsers = sortedUsers.slice(startIndex, endIndex);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleSearchTermChange = (term: string) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  const handleItemsPerPageChange = (items: number) => {
    setItemsPerPage(items);
    setCurrentPage(1);
  };

  const addUser = async (newUser: NewUser): Promise<void> => {
    try {
      await createUserMutation.mutateAsync(newUser);
    } catch (error) {
      console.error('유저 생성 실패:', error);
      throw error;
    }
  };

  const updateUser = async (
    userId: string,
    userData: Partial<NewUser>
  ): Promise<void> => {
    try {
      await updateUserMutation.mutateAsync({ userId, userData });
    } catch (error) {
      console.error('유저 업데이트 실패:', error);
      throw error;
    }
  };

  const deleteUser = async (userId: string): Promise<void> => {
    try {
      await deleteUserMutation.mutateAsync(userId);
    } catch (error) {
      console.error('유저 삭제 실패:', error);
      throw error;
    }
  };

  return {
    users,
    filteredUsers,
    sortedUsers,
    currentUsers,
    searchTerm,
    sortField,
    sortDirection,
    currentPage,
    itemsPerPage,
    totalPages,
    startIndex,
    endIndex,
    actions: {
      setSearchTerm: handleSearchTermChange,
      handleSort,
      setCurrentPage,
      setItemsPerPage: handleItemsPerPageChange,
      addUser,
      updateUser,
      deleteUser,
    },
    mutations: {
      createUser: createUserMutation,
      updateUser: updateUserMutation,
      deleteUser: deleteUserMutation,
    },
  };
};
